// src/pages/Production/SubcontractorDetail.jsx

import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { doc, onSnapshot, collection, getDoc, getDocs as getFirestoreDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { calculateSubcontractingCost } from '../../utils/subcontractorUtils';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const styles = {
    container: {
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        background: 'transparent'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '32px'
    },
    title: {
        fontSize: '32px',
        fontWeight: '700',
        color: '#0f172a',
        margin: 0,
        letterSpacing: '-0.5px'
    },
    backLink: {
        textDecoration: 'none',
        color: '#06C167',
        fontSize: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
    },
    headerActions: {
        display: 'flex',
        gap: '12px'
    },
    button: {
        padding: '12px 24px',
        border: 'none',
        background: 'linear-gradient(135deg, #06C167, #8b5cf6)',
        color: 'white',
        borderRadius: '12px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '600',
        boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
        transition: 'all 0.2s ease-in-out'
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: '1fr 2fr',
        gap: '30px',
        alignItems: 'start'
    },
    box: {
        background: 'white',
        padding: '32px',
        borderRadius: '16px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e2e8f0'
    },
    infoList: {
        listStyle: 'none',
        padding: 0
    },
    infoItem: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '16px 0',
        borderBottom: '1px solid #f1f5f9',
        alignItems: 'center'
    },
    infoLabel: {
        color: '#64748b',
        flexBasis: '40%',
        fontSize: '14px'
    },
    infoValue: {
        fontWeight: '500',
        color: '#0f172a',
        flexBasis: '60%',
        textAlign: 'right',
        fontSize: '14px'
    },
    statusBadge: {
        display: 'inline-block',
        padding: '6px 12px',
        borderRadius: '20px',
        fontSize: '14px',
        fontWeight: '600'
    },
    activeBadge: {
        backgroundColor: '#dcfce7',
        color: '#166534'
    },
    inactiveBadge: {
        backgroundColor: '#fee2e2',
        color: '#991b1b'
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        marginTop: '20px'
    },
    th: {
        background: '#f8fafc',
        padding: '12px 15px',
        textAlign: 'left',
        borderBottom: '2px solid #e2e8f0',
        color: '#64748b',
        fontSize: '12px',
        fontWeight: '600',
        textTransform: 'uppercase'
    },
    td: {
        padding: '15px',
        borderBottom: '1px solid #f1f5f9',
        verticalAlign: 'middle',
        fontSize: '14px'
    },
    trHover: {
        cursor: 'pointer',
        transition: 'background 0.2s'
    },
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000
    },
    modalContent: {
        background: 'white',
        padding: '30px',
        borderRadius: '16px',
        width: '90%',
        maxWidth: '600px',
        maxHeight: '90vh',
        overflowY: 'auto'
    },
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid #e2e8f0',
        paddingBottom: '15px',
        marginBottom: '20px'
    },
    deleteButton: {
        background: 'none',
        border: 'none',
        color: '#dc3545',
        cursor: 'pointer',
        fontSize: '20px'
    },
    balancePositive: {
        color: '#10b981',
        fontWeight: '600'
    },
    balanceNegative: {
        color: '#dc2626',
        fontWeight: '600'
    }
};

function SubcontractorDetail() {
    const { subcontractorId } = useParams();
    const { userData } = useAuth();
    const navigate = useNavigate();
    const [subcontractor, setSubcontractor] = useState(null);
    const [orders, setOrders] = useState([]);
    const [payments, setPayments] = useState([]);
    const [recipes, setRecipes] = useState([]); // New state for recipes
    const [stocks, setStocks] = useState([]); // New state for stocks
    const [loading, setLoading] = useState(true);

    // Fetch real data from the database
    useEffect(() => {
        if (!userData?.companyId || !subcontractorId) {
            setLoading(false);
            return;
        }

        const companyId = userData.companyId;

        // Fetch subcontractor
        const unsubscribeSubcontractor = onSnapshot(
            doc(db, 'companies', companyId, 'subcontractors', subcontractorId),
            (docSnap) => {
                if (docSnap.exists()) {
                    setSubcontractor({ id: docSnap.id, ...docSnap.data() });
                } else {
                    console.log('No such subcontractor!');
                }
                setLoading(false);
            },
            (error) => {
                console.error('Error fetching subcontractor:', error);
                setLoading(false);
            }
        );

        // Fetch orders for this subcontractor by querying production orders
        // that have stages with this subcontractor
        const unsubscribeOrders = onSnapshot(
            collection(db, 'companies', companyId, 'productionOrders'),
            (snap) => {
                const subcontractorOrders = [];
                snap.docs.forEach(doc => {
                    const order = { id: doc.id, ...doc.data() };
                    // Check if any stage has this subcontractor
                    if (order.stages && Array.isArray(order.stages)) {
                        order.stages.forEach(stage => {
                            let subcontractorInfo = null;

                            // Check for subcontractorDistribution (new format)
                            if (stage.subcontractorDistribution && Array.isArray(stage.subcontractorDistribution)) {
                                const dist = stage.subcontractorDistribution.find(d => d.supplierId === subcontractorId);
                                if (dist) {
                                    subcontractorInfo = {
                                        ...dist,
                                        stageName: stage.name,
                                        stageId: stage.id,
                                        status: dist.status || 'pending',
                                        quantity: dist.completedQuantity || dist.quantity || 0
                                    };
                                }
                            }
                            // Check for single subcontractor (old format)
                            else if (stage.subcontractor && stage.subcontractor.supplierId === subcontractorId) {
                                subcontractorInfo = {
                                    ...stage.subcontractor,
                                    stageName: stage.name,
                                    stageId: stage.id,
                                    status: stage.status || 'pending',
                                    quantity: stage.outputProduced?.quantity || 0,
                                    unit: stage.outputProduced?.unit || 'Adet'
                                };
                            }
                            // Check for subcontractors array (fallback)
                            else if (stage.subcontractors && Array.isArray(stage.subcontractors)) {
                                const sub = stage.subcontractors.find(s => s.supplierId === subcontractorId);
                                if (sub) {
                                    subcontractorInfo = {
                                        ...sub,
                                        stageName: stage.name,
                                        stageId: stage.id,
                                        status: stage.status || 'pending',
                                        quantity: stage.outputProduced?.quantity || 0,
                                        unit: stage.outputProduced?.unit || 'Adet'
                                    };
                                }
                            }

                            if (subcontractorInfo) {
                                // Calculate tariff cost using the actual completed quantity
                                const tariffCost = calculateSubcontractingCost(
                                    { costTariff: subcontractor?.costTariff || [] },
                                    subcontractorInfo.unit || 'Adet',
                                    subcontractorInfo.quantity // This is the completed quantity
                                );

                                // Get labor cost from subcontractor info (could be in different locations)
                                const laborCost = (subcontractorInfo.laborCost ||
                                    (stage.subcontractor ? stage.subcontractor.laborCost : 0) ||
                                    0) * subcontractorInfo.quantity;

                                // Total cost is tariff + labor
                                const totalCost = tariffCost + laborCost;

                                subcontractorOrders.push({
                                    id: `${order.id}-${stage.id}`,
                                    productionOrderId: order.id,
                                    orderNumber: order.orderNumber || `#${order.id.substring(0, 8)}`,
                                    product: stage.outputProduced?.product || order.product || 'Ürün belirtilmemiş',
                                    quantity: subcontractorInfo.quantity,
                                    unit: subcontractorInfo.unit || 'Adet',
                                    cost: totalCost,
                                    tariffCost: tariffCost,
                                    laborCost: laborCost,
                                    status: subcontractorInfo.status,
                                    orderDate: order.createdAt,
                                    stageName: subcontractorInfo.stageName,
                                    stageId: subcontractorInfo.stageId
                                });
                            }
                        });
                    }
                });
                setOrders(subcontractorOrders);
            }
        );

        // Fetch recipes that use this subcontractor
        const fetchRecipes = async () => {
            try {
                const recipesSnapshot = await getFirestoreDocs(collection(db, 'companies', companyId, 'recipes'));
                const subcontractorRecipes = [];

                recipesSnapshot.docs.forEach(doc => {
                    const recipe = { id: doc.id, ...doc.data() };
                    let usesSubcontractor = false;

                    // Check if any stage in the recipe uses this subcontractor
                    if (recipe.stages && Array.isArray(recipe.stages)) {
                        recipe.stages.forEach(stage => {
                            if (stage.type === 'subcontracting') {
                                // Check for subcontractorDistribution (new format)
                                if (stage.subcontractorDistribution && Array.isArray(stage.subcontractorDistribution)) {
                                    if (stage.subcontractorDistribution.some(d => d.supplierId === subcontractorId)) {
                                        usesSubcontractor = true;
                                    }
                                }
                                // Check for single subcontractor (old format)
                                else if (stage.subcontractor && stage.subcontractor.supplierId === subcontractorId) {
                                    usesSubcontractor = true;
                                }
                                // Check for subcontractors array (fallback)
                                else if (stage.subcontractors && Array.isArray(stage.subcontractors)) {
                                    if (stage.subcontractors.some(s => s.supplierId === subcontractorId)) {
                                        usesSubcontractor = true;
                                    }
                                }
                            }
                        });
                    }

                    if (usesSubcontractor) {
                        subcontractorRecipes.push(recipe);
                    }
                });

                setRecipes(subcontractorRecipes);
            } catch (error) {
                console.error('Error fetching recipes:', error);
            }
        };

        fetchRecipes();

        // Fetch stocks
        const unsubscribeStocks = onSnapshot(
            collection(db, 'companies', companyId, 'subcontractors', subcontractorId, 'stocks'),
            (snap) => {
                setStocks(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            }
        );

        // For payments, we would typically fetch from a payments collection
        // For now, we'll leave it empty as it would require a more complex implementation
        setPayments([]);

        return () => {
            unsubscribeSubcontractor();
            unsubscribeOrders();
            unsubscribeStocks();
        };
    }, [userData, subcontractorId]);

    const getStatusBadge = (isActive) => {
        return isActive ? (
            <span style={{ ...styles.statusBadge, ...styles.activeBadge }}>Aktif</span>
        ) : (
            <span style={{ ...styles.statusBadge, ...styles.inactiveBadge }}>Pasif</span>
        );
    };

    const getOrderStatusBadge = (status) => {
        // Handle production stage statuses
        if (status === 'pending' || status === 'Beklemede') {
            return <span style={{
                background: '#dbeafe',
                color: '#1e40af',
                padding: '4px 8px',
                borderRadius: '12px',
                fontSize: '12px'
            }}>Beklemede</span>;
        }
        if (status === 'inProgress' || status === 'Üretimde' || status === 'In Progress') {
            return <span style={{
                background: '#fef3c7',
                color: '#92400e',
                padding: '4px 8px',
                borderRadius: '12px',
                fontSize: '12px'
            }}>Devam Ediyor</span>;
        }
        if (status === 'completed' || status === 'Tamamlandı' || status === 'Delivered') {
            return <span style={{
                background: '#dcfce7',
                color: '#166534',
                padding: '4px 8px',
                borderRadius: '12px',
                fontSize: '12px'
            }}>Tamamlandı</span>;
        }
        if (status === 'cancelled' || status === 'Cancelled' || status === 'İptal Edildi') {
            return <span style={{
                background: '#fee2e2',
                color: '#991b1b',
                padding: '4px 8px',
                borderRadius: '12px',
                fontSize: '12px'
            }}>İptal Edildi</span>;
        }

        // Handle subcontracting order statuses
        switch (status) {
            case 'Ordered':
                return <span style={{
                    background: '#dbeafe',
                    color: '#1e40af',
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '12px'
                }}>Sipariş Verildi</span>;
            case 'In Progress':
                return <span style={{
                    background: '#fef3c7',
                    color: '#92400e',
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '12px'
                }}>Devam Ediyor</span>;
            case 'Delivered':
                return <span style={{
                    background: '#dcfce7',
                    color: '#166534',
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '12px'
                }}>Teslim Edildi</span>;
            case 'Cancelled':
                return <span style={{
                    background: '#fee2e2',
                    color: '#991b1b',
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '12px'
                }}>İptal Edildi</span>;
            default:
                return <span style={{
                    background: '#f3f4f6',
                    color: '#6b7280',
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '12px'
                }}>{status}</span>;
        }
    };

    const formatCurrency = (amount, currency) => {
        const formatter = new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: currency || 'TRY'
        });
        return formatter.format(amount);
    };

    const formatDate = (date) => {
        if (!date) return '-';
        // Handle Firestore timestamps
        if (date.toDate) {
            return date.toDate().toLocaleDateString('tr-TR');
        }
        // Handle regular Date objects
        if (date instanceof Date) {
            return date.toLocaleDateString('tr-TR');
        }
        // Handle string dates
        if (typeof date === 'string') {
            const parsedDate = new Date(date);
            if (!isNaN(parsedDate.getTime())) {
                return parsedDate.toLocaleDateString('tr-TR');
            }
        }
        return '-';
    };

    // Function to generate subcontractor production instruction PDF
    const generateProductionInstruction = async () => {
        try {
            // Fetch company information
            let companyInfo = null;
            if (userData?.companyId) {
                const companyDoc = await getDoc(doc(db, 'companies', userData.companyId));
                if (companyDoc.exists()) {
                    companyInfo = companyDoc.data();
                }
            }

            const doc = new jsPDF();

            // Add title
            doc.setFontSize(18);
            doc.text('FASON ÜRETİM TALİMATI', 105, 20, null, null, 'center');

            // Add company information
            doc.setFontSize(10);
            doc.text('Firma: ' + (companyInfo?.companyName || userData?.companyName || 'Bilinmiyor'), 20, 30);
            doc.text('Tarih: ' + new Date().toLocaleDateString('tr-TR'), 150, 30);

            // Add subcontractor information
            doc.setFontSize(12);
            doc.text(`Fason Tedarikçi: ${subcontractor.name}`, 20, 45);

            // Group orders by production order ID
            const groupedOrders = {};
            orders.forEach(order => {
                const orderId = order.productionOrderId || 'Belirsiz Sipariş';
                if (!groupedOrders[orderId]) {
                    groupedOrders[orderId] = {
                        orderNumber: order.orderNumber || orderId,
                        products: {}
                    };
                }

                // Group products within each order
                const productKey = order.product || 'Ürün belirtilmemiş';
                if (!groupedOrders[orderId].products[productKey]) {
                    groupedOrders[orderId].products[productKey] = {
                        totalQuantity: 0,
                        stages: {}
                    };
                }

                // Add to total quantity
                groupedOrders[orderId].products[productKey].totalQuantity += (order.quantity || 0);

                // Track stages
                const stageKey = order.stageName || 'Aşama belirtilmemiş';
                if (!groupedOrders[orderId].products[productKey].stages[stageKey]) {
                    groupedOrders[orderId].products[productKey].stages[stageKey] = 0;
                }
                groupedOrders[orderId].products[productKey].stages[stageKey] += (order.quantity || 0);
            });

            let currentY = 55;

            // Add each production order group
            Object.keys(groupedOrders).forEach((orderId, orderIndex) => {
                const orderData = groupedOrders[orderId];

                // Check if we need a new page
                if (currentY > 230) {
                    doc.addPage();
                    currentY = 20;
                }

                // Add order header
                doc.setFontSize(14);
                doc.text(`${orderIndex + 1}. ${orderData.orderNumber}`, 20, currentY);
                currentY += 10;

                // Add each product in this order
                Object.keys(orderData.products).forEach((product, productIndex) => {
                    const productData = orderData.products[product];

                    // Check if we need a new page
                    if (currentY > 240) {
                        doc.addPage();
                        currentY = 20;
                    }

                    // Add product information
                    doc.setFontSize(12);
                    doc.text(`  ${productIndex + 1}. ${product}`, 25, currentY);
                    currentY += 8;

                    doc.setFontSize(11);
                    doc.text(`     Toplam Miktar: ${productData.totalQuantity} adet`, 30, currentY);
                    currentY += 7;

                    // Add stage information
                    Object.keys(productData.stages).forEach(stage => {
                        doc.text(`     • ${stage}: ${productData.stages[stage]} adet`, 35, currentY);
                        currentY += 6;
                    });

                    currentY += 3;
                });

                currentY += 5;
            });

            // Add special instructions section
            if (currentY > 230) {
                doc.addPage();
                currentY = 20;
            }

            currentY += 10;
            doc.setFontSize(14);
            doc.text('ÖZEL TALİMATLAR', 20, currentY);
            currentY += 15;

            doc.setFontSize(11);
            doc.text('1. Ürünler bu belgede belirtilen miktarlarda üretilecektir.', 20, currentY);
            currentY += 8;
            doc.text('2. Üretim tamamlandığında firma yetkilisine bildirilecektir.', 20, currentY);
            currentY += 8;
            doc.text('3. Kalite kontrolleri üretim sırasında yapılacaktır.', 20, currentY);
            currentY += 8;
            doc.text('4. Her üretim emri için ayrı ayrı üretim yapılmalıdır.', 20, currentY);
            currentY += 8;

            // Add signature section
            if (currentY > 230) {
                doc.addPage();
                currentY = 20;
            }

            currentY += 20;
            doc.line(20, currentY, 190, currentY); // Signature line
            doc.text('İmza:', 20, currentY + 10);
            doc.text('Tarih:', 150, currentY + 10);

            // Save the PDF
            doc.save(`Fason_Uretim_Talimati_${subcontractor.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
        } catch (error) {
            console.error('Error generating production instruction:', error);
            alert('Üretim talimatı oluşturulurken bir hata oluştu: ' + error.message);
        }
    };

    if (loading) return <p>Yükleniyor...</p>;
    if (!subcontractor) return <h2>Fason Tedarikçi Bulunamadı</h2>;

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <Link to="/uretim/fason-tedarikciler" style={styles.backLink}>
                    ← Fason Tedarikçiler
                </Link>
                <div style={styles.headerActions}>
                    <button
                        onClick={generateProductionInstruction}
                        style={{ ...styles.button, background: '#10b981' }}
                    >
                        Üretim Talimatı
                    </button>
                    <button
                        onClick={() => alert('Fason tedarikçi pasif hale getirildi!')}
                        style={{ ...styles.button, background: '#ef4444' }}
                    >
                        Pasif Yap
                    </button>
                </div>
            </div>

            <div style={styles.grid}>
                <div>
                    <div style={styles.box}>
                        <h2 style={styles.title}>{subcontractor.name}</h2>
                        <ul style={styles.infoList}>
                            <li style={styles.infoItem}>
                                <span style={styles.infoLabel}>Durum</span>
                                <span style={styles.infoValue}>
                                    {getStatusBadge(subcontractor.isActive !== false)}
                                </span>
                            </li>
                            <li style={styles.infoItem}>
                                <span style={styles.infoLabel}>E-posta</span>
                                <span style={styles.infoValue}>{subcontractor.email || '-'}</span>
                            </li>
                            <li style={styles.infoItem}>
                                <span style={styles.infoLabel}>Telefon</span>
                                <span style={styles.infoValue}>{subcontractor.phone || '-'}</span>
                            </li>
                            <li style={styles.infoItem}>
                                <span style={styles.infoLabel}>Adres</span>
                                <span style={styles.infoValue}>{subcontractor.address || '-'}</span>
                            </li>
                            <li style={styles.infoItem}>
                                <span style={styles.infoLabel}>Vergi Dairesi</span>
                                <span style={styles.infoValue}>{subcontractor.taxOffice || '-'}</span>
                            </li>
                            <li style={styles.infoItem}>
                                <span style={styles.infoLabel}>Vergi Numarası</span>
                                <span style={styles.infoValue}>{subcontractor.taxNumber || '-'}</span>
                            </li>
                            <li style={{ ...styles.infoItem, padding: '20px 0' }}>
                                <span style={styles.infoLabel}>Bakiye</span>
                                <span style={{ ...styles.infoValue, ...(subcontractor.balance < 0 ? styles.balanceNegative : styles.balancePositive), fontSize: '20px', fontWeight: 'bold' }}>
                                    {formatCurrency(subcontractor.balance || 0, 'TRY')}
                                </span>
                            </li>
                        </ul>
                    </div>

                    <div style={{ ...styles.box, marginTop: '30px' }}>
                        <h3 style={{ ...styles.title, fontSize: '20px', marginBottom: '20px' }}>Üretim Türleri</h3>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            {subcontractor.productionTypes && subcontractor.productionTypes.map((type, index) => (
                                <span key={index} style={{
                                    background: '#e2e8f0',
                                    padding: '6px 12px',
                                    borderRadius: '16px',
                                    fontSize: '14px'
                                }}>
                                    {type}
                                </span>
                            ))}
                            {(!subcontractor.productionTypes || subcontractor.productionTypes.length === 0) && (
                                <span style={{ color: '#64748b', fontStyle: 'italic' }}>
                                    Üretim türü tanımlanmamış
                                </span>
                            )}
                        </div>
                    </div>

                    <div style={{ ...styles.box, marginTop: '30px' }}>
                        <h3 style={{ ...styles.title, fontSize: '20px', marginBottom: '20px' }}>Maliyet Tarifesi</h3>
                        {subcontractor.costTariff && subcontractor.costTariff.length > 0 ? (
                            <table style={{ ...styles.table, margin: 0 }}>
                                <thead>
                                    <tr>
                                        <th style={{ ...styles.th, background: '#f1f5f9' }}>Tür</th>
                                        <th style={{ ...styles.th, background: '#f1f5f9' }}>Değer</th>
                                        <th style={{ ...styles.th, background: '#f1f5f9' }}>Birim</th>
                                        <th style={{ ...styles.th, background: '#f1f5f9' }}>Para Birimi</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {subcontractor.costTariff.map((item) => (
                                        <tr key={item.id || item.type + item.value}>
                                            <td style={styles.td}>
                                                {item.type === 'per_unit' && 'Adet Başına'}
                                                {item.type === 'per_kg' && 'Kilo Başına'}
                                                {item.type === 'per_job' && 'İş Başına'}
                                                {!item.type && '-'}
                                            </td>
                                            <td style={styles.td}>{item.value || 0}</td>
                                            <td style={styles.td}>{item.unit || '-'}</td>
                                            <td style={styles.td}>{item.currency || 'TRY'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <p style={{ color: '#64748b', fontStyle: 'italic' }}>
                                Maliyet tarifesi tanımlanmamış
                            </p>
                        )}
                    </div>
                </div>

                <div>
                    <div style={{ ...styles.box, marginBottom: '30px' }}>
                        <h3 style={{ ...styles.title, fontSize: '20px', marginBottom: '20px' }}>📦 Mevcut Stok Durumu</h3>
                        {stocks.length > 0 ? (
                            <table style={styles.table}>
                                <thead>
                                    <tr>
                                        <th style={styles.th}>Ürün</th>
                                        <th style={styles.th}>Miktar</th>
                                        <th style={styles.th}>Son İşlem</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {stocks.map(stock => (
                                        <tr key={stock.id} style={styles.trHover}>
                                            <td style={styles.td}>
                                                <div style={{ fontWeight: '500' }}>{stock.productName}</div>
                                            </td>
                                            <td style={{ ...styles.td, fontWeight: '600', color: stock.quantity > 0 ? '#166534' : '#991b1b' }}>
                                                {stock.quantity} {stock.unit}
                                            </td>
                                            <td style={{ ...styles.td, fontSize: '12px', color: '#64748b' }}>
                                                {formatDate(stock.lastUpdated)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div style={{
                                padding: '24px',
                                textAlign: 'center',
                                background: '#f8fafc',
                                borderRadius: '12px',
                                color: '#64748b',
                                fontSize: '14px'
                            }}>
                                Bu tedarikçinin elinde henüz kaydedilmiş ürün bulunmamaktadır.
                            </div>
                        )}
                    </div>

                    <div style={styles.box}>
                        <h3 style={{ ...styles.title, fontSize: '20px', marginBottom: '20px' }}>Fason Siparişleri</h3>

                        {orders.length > 0 ? (
                            <table style={styles.table}>
                                <thead>
                                    <tr>
                                        <th style={styles.th}>Ürün</th>
                                        <th style={styles.th}>Aşama</th>
                                        <th style={styles.th}>Miktar</th>
                                        <th style={styles.th}>Tarife</th>
                                        <th style={styles.th}>İşçilik</th>
                                        <th style={styles.th}>Toplam</th>
                                        <th style={styles.th}>Durum</th>
                                        <th style={styles.th}>Sipariş Tarihi</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {orders.map(order => (
                                        <tr
                                            key={order.id}
                                            style={styles.trHover}
                                            onClick={() => order.productionOrderId && navigate(`/uretim/emirler/${order.productionOrderId}`)}
                                        >
                                            <td style={styles.td}>
                                                <div style={{ fontWeight: '600' }}>{order.product || 'Ürün belirtilmemiş'}</div>
                                            </td>
                                            <td style={styles.td}>{order.stageName || '-'}</td>
                                            <td style={styles.td}>{order.quantity || 0} {order.unit || 'Adet'}</td>
                                            <td style={styles.td}>
                                                {order.tariffCost > 0 && order.quantity > 0 ? (
                                                    <div>
                                                        <div>{formatCurrency(order.tariffCost || 0, 'TRY')}</div>
                                                        <div style={{ fontSize: '11px', color: '#64748b' }}>
                                                            ({formatCurrency((order.tariffCost / order.quantity) || 0, 'TRY')}/adet)
                                                        </div>
                                                    </div>
                                                ) : (
                                                    formatCurrency(order.tariffCost || 0, 'TRY')
                                                )}
                                            </td>
                                            <td style={styles.td}>
                                                {order.laborCost > 0 && order.quantity > 0 ? (
                                                    <div>
                                                        <div>{formatCurrency(order.laborCost || 0, 'TRY')}</div>
                                                        <div style={{ fontSize: '11px', color: '#64748b' }}>
                                                            ({formatCurrency((order.laborCost / order.quantity) || 0, 'TRY')}/adet)
                                                        </div>
                                                    </div>
                                                ) : (
                                                    formatCurrency(order.laborCost || 0, 'TRY')
                                                )}
                                            </td>
                                            <td style={{ ...styles.td, fontWeight: '600' }}>
                                                {formatCurrency(order.cost || 0, 'TRY')}
                                            </td>
                                            <td style={styles.td}>
                                                {getOrderStatusBadge(order.status || 'Ordered')}
                                            </td>
                                            <td style={styles.td}>
                                                {order.orderDate ? formatDate(order.orderDate) : '-'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <p style={{ color: '#64748b', fontStyle: 'italic' }}>
                                Henüz fason siparişi oluşturulmamış.
                            </p>
                        )}
                    </div>

                    {/* New section for showing recipes that use this subcontractor */}
                    <div style={{ ...styles.box, marginTop: '30px' }}>
                        <h3 style={{ ...styles.title, fontSize: '20px', marginBottom: '20px' }}>Üretilen Reçeteler</h3>

                        {recipes.length > 0 ? (
                            <table style={styles.table}>
                                <thead>
                                    <tr>
                                        <th style={styles.th}>Reçete Adı</th>
                                        <th style={styles.th}>Ürün</th>
                                        <th style={styles.th}>Aşama Sayısı</th>
                                        <th style={styles.th}>Durum</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {recipes.map(recipe => (
                                        <tr
                                            key={recipe.id}
                                            style={styles.trHover}
                                            onClick={() => navigate(`/uretim/reçeteler/${recipe.id}`)}
                                        >
                                            <td style={styles.td}>
                                                <div style={{ fontWeight: '600' }}>{recipe.name}</div>
                                            </td>
                                            <td style={styles.td}>{recipe.product}</td>
                                            <td style={styles.td}>{(recipe.stages || []).length} aşama</td>
                                            <td style={styles.td}>
                                                {getStatusBadge(recipe.status === 'active')}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <p style={{ color: '#64748b', fontStyle: 'italic' }}>
                                Bu fason tedarikçi hiçbir reçetede kullanılmamış.
                            </p>
                        )}
                    </div>

                    <div style={{ ...styles.box, marginTop: '30px' }}>
                        <h3 style={{ ...styles.title, fontSize: '20px', marginBottom: '20px' }}>Ödemeler</h3>

                        {payments.length > 0 ? (
                            <table style={styles.table}>
                                <thead>
                                    <tr>
                                        <th style={styles.th}>Tarih</th>
                                        <th style={styles.th}>Açıklama</th>
                                        <th style={styles.th}>Tutar</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {payments.map(payment => (
                                        <tr key={payment.id} style={styles.trHover}>
                                            <td style={styles.td}>{payment.date ? formatDate(payment.date) : '-'}</td>
                                            <td style={styles.td}>{payment.description || '-'}</td>
                                            <td style={{ ...styles.td, fontWeight: '600', color: '#10b981' }}>
                                                +{formatCurrency(payment.amount || 0, payment.currency || 'TRY')}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <p style={{ color: '#64748b', fontStyle: 'italic' }}>
                                Henüz ödeme kaydedilmemiş.
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default SubcontractorDetail;